/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Papa from 'papaparse';

const INTERVAL_VALUE_IGNORED_COLUMNS = new Set([
    '',
    'local server time',
    'state',
    'timestamp',
]);

export interface ParsedIntervalValueSeries {
    column: string;
    unit: string;
    urn: string;
}

export interface ParsedIntervalValueCsvRow {
    timestamp: string;
    values: Record<string, string>;
}

export interface ParsedIntervalValueCsv {
    rows: ParsedIntervalValueCsvRow[];
    series: ParsedIntervalValueSeries[];
}

function parseIntervalValueSeries(column: string): ParsedIntervalValueSeries {
    const match = column.trim().match(/^(.*?)\s+\[([^\]]+)]$/);

    return {
        column,
        unit: match?.[2]?.trim() ?? '',
        urn: match?.[1]?.trim() ?? column.trim(),
    };
}

export function parseIntervalValueCsv(csv: string): ParsedIntervalValueCsv {
    const parseResult = Papa.parse<Record<string, string>>(csv, {
        delimiter: ';',
        header: true,
        skipEmptyLines: true,
    });
    const fields = parseResult.meta.fields ?? [];
    const series = fields
        .filter((field) => !INTERVAL_VALUE_IGNORED_COLUMNS.has(field.trim()))
        .map(parseIntervalValueSeries);

    return {
        rows: parseResult.data.map((row) => ({
            timestamp: row.timestamp?.trim() ?? '',
            values: Object.fromEntries(
                series.map(({ column, urn }) => [urn, row[column]?.trim() ?? '']),
            ),
        })),
        series,
    };
}
